import { ActionItemsViewModel, IRange, IUser } from '@ViewModels';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { NotesDateFilterOptionValue, NotesSortOptionValue } from '../../../../models';
import { formatDateRangeString } from '../../../../models/UiUtils';
import { Select, SelectContent, SelectItem, SelectTrigger } from '../../../components-v2/Select';
import { DateRangePickerSideModal } from '../../DateRangePickerSide';
import { Portal } from '../../Portal';
import { TextInput } from '../../TextInput';
import { SearchIcon } from '../../svgs/icons/SearchIcon';
import { ActionItemsFilter, ActionItemsFilterValue } from '../ActionItemsFilter';
import { styleSheet } from './styles';

export const NoteDateOptions: { label: string; value: NotesDateFilterOptionValue }[] = [
	{ label: 'Filter by Date', value: 'date' },
];
export const NoteSortOptions: { label: string; value: NotesSortOptionValue }[] = [
	{ label: 'Due date (oldest first)', value: 'DueDate-asc' },
	{ label: 'Due date (newest first)', value: 'DueDate-dsc' },
	{ label: 'Last Modified date (oldest first)', value: 'LastModifiedDate-asc' },
	{ label: 'Last Modified date (newest first)', value: 'LastModifiedDate-dsc' },
	{ label: 'Creation date (oldest first)', value: 'CreationDate-asc' },
	{ label: 'Creation date (newest first)', value: 'CreationDate-dsc' },
];

interface IProps {
	actionItemsVm?: ActionItemsViewModel;
	className?: string;
	onClearInput?(): void;
	onFilterSelectedOptionChanged?(selectedFilterValue: ActionItemsFilterValue, user?: IUser): void;
	onSearchInputBlur?(e: React.FocusEvent<HTMLInputElement>): void;
	onSearchInputChanged?(e: React.ChangeEvent<HTMLInputElement>): void;
	onSearchInputKeyDown?(e: React.KeyboardEvent<HTMLInputElement>): void;
	onRangeChanged?(range: IRange<Date | null>): void;
	onSortSelectedOptionChanged?(selectedSortOption: NotesSortOptionValue): void;
	portalDestination?: HTMLElement | string;
	selectedFilterValue?: ActionItemsFilterValue;
}

interface IState {
	selectedFilterValue?: ActionItemsFilterValue;
	dateRange: IRange<Date>;
	showDateSelector: boolean;
	selectedDateRangeText: string;
	selectedSortOption: { label: string; value: NotesSortOptionValue };
}

const DefaultDateRangeText = 'Filter by Date';

class _ActionItemsListHeader extends React.Component<IProps, IState> {
	public readonly state: IState = {
		selectedFilterValue: this.props.selectedFilterValue,
		dateRange: { start: new Date(), end: new Date() },
		showDateSelector: false,
		selectedDateRangeText: DefaultDateRangeText,
		selectedSortOption: NoteSortOptions[0],
	};
	public componentDidMount() {
		this.props?.onSortSelectedOptionChanged?.(this.state.selectedSortOption.value);
	}
	public UNSAFE_componentWillReceiveProps(nextProps: IProps) {
		this.setState({ selectedFilterValue: nextProps.selectedFilterValue });
	}

	public render() {
		const {
			actionItemsVm,
			className,
			onClearInput,
			onSearchInputBlur,
			onSearchInputChanged,
			onSearchInputKeyDown,
			portalDestination,
			onRangeChanged,
		} = this.props;
		const { selectedFilterValue, dateRange, showDateSelector, selectedDateRangeText, selectedSortOption } = this.state;
		const showSearch =
			selectedFilterValue === ActionItemsFilterValue.CurrentUser || selectedFilterValue === ActionItemsFilterValue.All;
		if (!actionItemsVm) {
			return null;
		}

		const onRangeChange = (range: IRange<Date>) => {
			const nextDateRange = {
				start: range.start,
				end: range.end,
			};
			this.setState({ dateRange: nextDateRange });
			onRangeChanged?.(nextDateRange);
		};

		const setShowDateSelector = (show: boolean) => {
			this.setState({ showDateSelector: show });
		};

		const setSelectedDateRangeText = (text: string) => {
			this.setState({ selectedDateRangeText: text });
		};

		const setSelectedSortOption = (option: { label: string; value: NotesSortOptionValue }) => {
			this.setState({ selectedSortOption: option });
			this.props?.onSortSelectedOptionChanged?.(option.value);
		};

		const header = (
			<div className={`${css(styleSheet.header)} action-items-list-header ${className || ''}`}>
				<div>
					{showSearch ? (
						<div className={css(styleSheet.headerSearchWrap)}>
							<TextInput
								inputId='action-items-search-field'
								className={`${css(styleSheet.searchField)} action-items-search-field`}
								placeholder='Search action items'
								type='text'
								onBlur={onSearchInputBlur}
								onChange={onSearchInputChanged}
								value={actionItemsVm.searchQuery || ''}
								onKeyDown={onSearchInputKeyDown}
								leftAccessory={<SearchIcon className={css(styleSheet.icon)} />}
							/>
							{actionItemsVm?.searchQuery?.length > 0 && (
								<button onClick={onClearInput} className={css(styleSheet.clear)}>
									Clear
								</button>
							)}
						</div>
					) : null}
				</div>
				<div className={css(styleSheet.searchContainer)}>
					<Select
						value={selectedSortOption.value}
						onValueChange={value => {
							const option = NoteSortOptions.find(o => o.value === value);
							if (option) {
								setSelectedSortOption(option);
							}
						}}
					>
						<SelectTrigger styles={styleSheet.sortSelect}>
							<span>{selectedSortOption.label}</span>
						</SelectTrigger>
						<SelectContent>
							{NoteSortOptions.map(option => (
								<SelectItem key={option.value} value={option.value}>
									{option.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					<Select
						onValueChange={() => {
							setShowDateSelector(true);
						}}
					>
						<SelectTrigger styles={styleSheet.dateSelect}>
							<span>{selectedDateRangeText}</span>
						</SelectTrigger>
						<SelectContent>
							{NoteDateOptions.map(option => (
								<SelectItem key={option.value} value={option.value}>
									{option.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					<DateRangePickerSideModal
						isOpen={showDateSelector}
						onRequestClose={(clear = false) => {
							setShowDateSelector(false);
							if (clear) {
								setSelectedDateRangeText(DefaultDateRangeText);

								onRangeChange({ start: null, end: null });
							} else {
								setSelectedDateRangeText(formatDateRangeString(dateRange));
							}
						}}
						onRangeChange={onRangeChange}
						range={dateRange}
					/>
					<ActionItemsFilter
						onFilterValueChanged={this.onFilterSelectedOptionChanged}
						selectedFilterValue={selectedFilterValue}
					/>
				</div>
			</div>
		);

		return portalDestination ? <Portal destination={portalDestination}>{header}</Portal> : header;
	}

	private onFilterSelectedOptionChanged = (selectedFilterValue: ActionItemsFilterValue, user?: IUser) => {
		const { onFilterSelectedOptionChanged } = this.props;
		this.setState({ selectedFilterValue });
		if (onFilterSelectedOptionChanged) {
			onFilterSelectedOptionChanged(selectedFilterValue, user);
		}
	};
}

export const ActionItemsListHeader = observer(_ActionItemsListHeader);
